import { create } from 'zustand';
import i18n from "i18next";

import { apiDomain, basename } from '../setting';
import useModalStore from './useModalStore';
import useAuthStore from './useAuthStore';
import useLoadingStore from './useLoadingStore';
import withErrorHandling from '../function/withErrorHandling';

// Step Sequence of apply
// 1_1 => 1_2 => 2_2 => 2_4 => 2_3 => 2_5 => 3_1 => 4_1

const useApplyStore = create((set, get) => ({
    step: "loading",
    // step: "submitFail",
    setStep: (value) => set((state) => {
        return { step: value }
    }),
    resetStep: () => set((state) => {
        return { step: "loading" }
    }),
    resetApplyData: () => set((state) => {
        return {
            refno: "",
            hospitalList: [],
            isQ1Option1Active: false,
            isQ1Option2Active: false,
            q1OptionError: false,
            isQ2Option1Active: false,
            isQ2Option2Active: false,
            q2OptionError: false,
            hospital: {},
            hospitalText: "",
            hospitalError1: false,
            hospitalError2: false,
            step1_1_Q1: "",
            step1_1_Q1Error: false,
            step1_1_Q2_Type: "person",
            step1_1_Q2: "",
            step1_1_Q2Error: false,
            step1_1_Q3: "",
            step1_1_Q3Error: false,
            step1_1_Q4: "",
            step1_1_Q4Error: false,
            isStep2_1Option1Active: false,
            isStep2_1Option2Active: false,
            isStep2_1Option3Active: false,
            step2_1Error: false,
            step2_2Bnet: "",
            step2_2BnetError: false,
            step2_2MedicalDocuments: [],
            step2_2MedicalDocumentsError: false,
            step2_3_Q1: "",
            step2_3_Q1Error: false,
            step2_3_Q2: "",
            step2_3_Q2Error: false,
            step2_3_Q3: "",
            step2_3_Q3Error: false,
            step2_3_Q4: "",
            step2_3_Q4Error: false,
            step2_3_Q5: "",
            step2_3_Q5Error: false,
            step2_3_Q6: "",
            step2_3_Q6Error: false,
            step2_3_Remark: "",
            step2_4FirstVisit: "",
            step2_4FirstVisitError: false,
            step2_4AdmissionDate: "",
            step2_4AdmissionDateError: false,
            step2_4HousingClassOption1: "",
            step2_4HousingClassOption2: "",
            step2_4HousingClassOption3: "",
            step2_4HousingClassOption4: "",
            step2_4HousingClassOption5: "",
            step2_4HousingClassError: false,
            step2_4Diagnosis: "",
            step2_4DiagnosisError: false,
            step2_4SymptomDuration: "",
            step2_4SymptomDurationError: false,
            step2_5Option1Active: false,
            step2_5Option2Active: false,
            step2_5OptionError: false,
            step2_5DocterName: "",
            isStep2_5Valid: false,
            anesthesiaMethodOption1: false,
            anesthesiaMethodOption2: false,
            anesthesiaMethodOption3: false,
            anesthesiaMethodOption4: false,
            anesthesiaMethodOption5: false,
            anesthesiaMethodError: false,
            surgeryToBePerformed: "",
            surgeryToBePerformedError: false,
            isStep3_1Accepted: false,
            isStep4_1Accepted: false,
            step4_1Error: false,
            step2_2Attachid: "",
            isStep0Valid: false,
            isStep1_1Valid: false,
            isStep1_2Valid: false,
            isStep2_1Valid: false,
            isStep2_2Valid: false,
            isStep2_3Valid: false,
            isStep2_4Valid: false,
            isStep2_5Valid: false,
            isStep3_1Valid: false,
            isStep4_1Valid: false,
        }
    }),
    goNextStep: withErrorHandling(async () => {
        const { step, setStep } = get();
        const { isQ1Option1Active, isQ1Option2Active, isQ2Option1Active, isQ2Option2Active, hospital, step4_option1 } = get();
        const { checkIsStep0Valid, checkIsStep1_1Valid, checkIsStep2_1Valid, checkIsStep2_2Valid, checkIsStep2_3Valid, checkIsStep2_4Valid, checkIsStep2_5Valid, checkIsStep3_1Valid } = get();
        const { isStep2_1Option1Active, isStep2_1Option2Active } = get();
        const { isStep2_1Option3Active, setIsStep2_1Option3Active } = get();
        const { step1_1_saveForm, step2_1_saveForm, step2_2_saveForm, step2_3_saveForm, step2_4_saveForm, step2_5_saveForm } = get();
        const { getPreAuthForm, applicationSubmit, completePayment } = get();
        const { step2_4HousingClassOption4, step2_4HousingClassOption5 } = get();
        const { isModalStartApplyOpen, openModalInfo, openModalStartApply, closeModalStartApply } = useModalStore.getState();
        const { userAuthenticate, token } = useAuthStore.getState();
        const { setLoading } = useLoadingStore.getState();

        switch (step) {
            case "loading":
                setStep("0");
                break;
            case "0":
                if (isModalStartApplyOpen) {
                    closeModalStartApply();
                    setStep("1_1");
                } else if (checkIsStep0Valid()) {
                    openModalStartApply();
                }
                break;
            case "1_1":
                if (checkIsStep1_1Valid()) {
                    setLoading(true);
                    const success = await userAuthenticate();
                    if (success) {
                        await getPreAuthForm();
                        checkIsStep2_1Valid(true);
                    }
                    setLoading(false);
                }

                break;
            case "1_2":
                // setStep("2_1");
                setLoading(true);
                setIsStep2_1Option3Active(true);
                await step2_1_saveForm();
                setStep("2_2");
                checkIsStep2_2Valid(true);
                setLoading(false);
                break;
            case "2_1":
                if (checkIsStep2_1Valid()) {
                    setLoading(true);
                    await step2_1_saveForm();
                    setStep("2_2");
                    checkIsStep2_2Valid(true);
                    setLoading(false);
                }

                break;
            case "2_2":
                if (checkIsStep2_2Valid()) {
                    setLoading(true);
                    await step2_2_saveForm();
                    if (isStep2_1Option1Active) {
                        setStep("2_5");
                    } else if (isStep2_1Option2Active) {
                        setStep("2_3");
                    } else if (isStep2_1Option3Active) {
                        setStep("2_4");
                    }
                    setLoading(false);
                }

                break;
            case "2_3":
                if (checkIsStep2_3Valid()) {
                    setLoading(true);
                    await step2_3_saveForm();
                    setStep("2_5");
                    setLoading(false);
                }

                break;
            case "2_4":
                if (checkIsStep2_4Valid()) {
                    setLoading(true);
                    await step2_4_saveForm();
                    setStep("2_3");
                    setLoading(false);
                }

                break;
            case "2_5":
                if (checkIsStep2_5Valid()) {
                    setLoading(true);
                    await step2_5_saveForm();
                    setStep("3_1");
                    setLoading(false);
                }

                break;
            case "3_1":
                if (checkIsStep3_1Valid()) {
                    if (
                        isQ1Option1Active &&
                        isQ2Option1Active &&
                        hospital?.value !== "00000000000000000000000000000000" &&
                        !step2_4HousingClassOption4 &&
                        !step2_4HousingClassOption5
                    ) {
                        setStep("4_1");
                    } else {
                        setLoading(true);
                        const success = await applicationSubmit();
                        setLoading(false);

                        if (success) {
                            setStep("success");
                        } else {
                            setStep("submitFail");
                        }
                    }
                }
                break;
            case "4_1":
                setLoading(true);
                if (step4_option1) {
                    await completePayment();
                } else {
                    await fetch(`${apiDomain}/${basename}/api/preAuthCore/savePreAuthForm`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            "notToPaid": "Y",
                        })
                    });
                }

                const success = await applicationSubmit();
                setLoading(false);
                if (success) {
                    setStep("success");
                } else {
                    setStep("submitFail");
                }

                break;
            default:
                break;
        }
    }),
    goBackStep: () => set(() => {
        const step = get().step;
        const { isStep2_1Option1Active, isStep2_1Option3Active } = get();

        switch (step) {
            case "1_1":
                return { step: "0" }
            case "1_2":
                return { step: "1_1" }
            case "2_1":
                return { step: "1_2" }
            case "2_2":
                return { step: "2_1" }
            case "2_3":
                if (isStep2_1Option3Active) {
                    return { step: "2_4" }
                }

                return { step: "2_2" }
            case "2_4":
                return { step: "2_2" }
            case "2_5":
                if (isStep2_1Option1Active) {
                    return { step: "2_2" }
                }
                return { step: "2_3" }
            case "3_1":
                return { step: "2_5" }
            case "4_1":
                return { step: "3_1" }
            case "success":
                return { step: "4_1" }
            default:
                return { step: step }
        }
    }),

    applicationSubmit: async () => {
        const { token } = useAuthStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/applicationSubmit`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });
        const data = await response.json();

        if (data.status === "SUCCESS") {
            set({
                refno: data.form.refno,
            });
        }

        return data.status === "SUCCESS";
    },
    completePayment: async () => {
        const { token } = useAuthStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/completePayment`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });
        const data = await response.json();
        // console.log(data);
    },
    failPayment: async () => {
        const { token } = useAuthStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/failPayment`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });
        const data = await response.json();
        // console.log(data);
    },
    earliestAdmissionLOGDate: "",
    earliestAdmissionPCADate: "",
    maxAdmissionDate: "",
    getPreAuthForm: async () => {
        const { setStep } = get();
        const { step1_1_saveForm, step2_1_saveForm, step2_2_saveForm, step2_3_saveForm, step2_4_saveForm } = get();
        const { checkIsStep2_1Valid, checkIsStep2_2Valid, checkIsStep2_3Valid, checkIsStep2_4Valid, checkIsStep2_5Valid } = get();
        const { openModalConfirm } = useModalStore.getState();
        const { token } = useAuthStore.getState();
        const response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/getPreAuthForm`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });
        const data = await response.json();

        set({
            refno: data.form.refno,
            earliestAdmissionLOGDate: data.earliestAdmissionLOGDate,
            earliestAdmissionPCADate: data.earliestAdmissionPCADate,
            maxAdmissionDate: data.maxAdmissionDate,
        });

        if (data.form.operationloctype) {
            openModalConfirm({
                title: i18n.t("translation:apply.step1_1.popup.title"),
                text: i18n.t("translation:apply.step1_1.popup.content"),
                callback: () => {
                    set({
                        isStep2_1Option1Active: data.form.applicationtype === "DOCFILL",
                        isStep2_1Option2Active: data.form.applicationtype === "BNET",
                        isStep2_1Option3Active: data.form.applicationtype === "NOTSURE",

                        step2_2Bnet: data.form.bnetno || "",
                        step2_2MedicalDocuments: data.attachment,
                        step2_2Attachid: data.form.appattachid || "",

                        step2_3_Q1: data.form.surgeonfee || "",
                        step2_3_Q2: data.form.anesthesiologistfee || "",
                        step2_3_Q3: data.form.operatingrmfee || "",
                        step2_3_Q4: data.form.housetourfee || "",
                        step2_3_Q5: data.form.hosmisousfee || "",
                        step2_3_Q6: data.form.noofdays || "",
                        step2_3_Remark: data.form.surgeryBudgetRemark || "",

                        step2_4FirstVisit: data.form.firstvisitdt,
                        step2_4AdmissionDate: data.form.admissiondt,
                        isQ1Option1Active: data.form.operationloctype === "HOSPITAL",
                        isQ1Option2Active: data.form.operationloctype === "DAYCENTRE",
                        isQ2Option1Active: data.form.operationloctype === "DAYCENTRE" ? "" : data.form.location === "HKMU",
                        isQ2Option2Active: data.form.operationloctype === "DAYCENTRE" ? "" : data.form.location === "OTHER",
                        hospital: {
                            value: data.form.hospitalid,
                        },
                        hospitalText: data.form.otherhopsital,
                        step2_4HousingClassOption1: data.form.housingclass === "REGULAR",
                        step2_4HousingClassOption2: data.form.housingclass === "SEMI",
                        step2_4HousingClassOption3: data.form.housingclass === "PRIVATE",
                        step2_4HousingClassOption4: data.form.housingclass === "DAYCASE",
                        step2_4HousingClassOption5: data.form.housingclass === "OUTPATIENT_CLINICAL",
                        step2_4Diagnosis: data.form.diagnosis,
                        step2_4SymptomDuration: data.form.medsigndt,
                        anesthesiaMethodOption1: data.form.Ansthsimthd === "NONEED",
                        anesthesiaMethodOption2: data.form.Ansthsimthd === "PARTIAL",
                        anesthesiaMethodOption3: data.form.Ansthsimthd === "FULL",
                        anesthesiaMethodOption4: data.form.Ansthsimthd === "MONITOR",
                        anesthesiaMethodOption5: data.form.Ansthsimthd === "UNKNOWN",
                        surgeryToBePerformed: data.form.oprtn,

                        step2_5Option1Active: data.form.isBxNetWork === "Y",
                        step2_5Option2Active: data.form.isBxNetWork === "N",
                        step2_5DocterName: data.form.attendDoctor,
                    });

                    checkIsStep2_1Valid(true);
                    checkIsStep2_2Valid(true);
                    checkIsStep2_3Valid(true);
                    checkIsStep2_4Valid(true);
                    checkIsStep2_5Valid(true);

                    if (!data.form.admissiondt && (data.form.step === "2_3" || data.form.step === "2_5")) {
                        setStep("2_4");
                    } else {
                        setStep(data.form.step || "1_2");
                    }
                },
                cancelCallback: () => {
                    setStep("1_2");

                    step1_1_saveForm();

                    fetch(`${apiDomain}/${basename}/api/preAuthCore/savePreAuthForm`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            "step": "",
                            "applicationtype": "",
                            "bnetno": "",
                            "appattachid": "",
                            "surgeonfee": "",
                            "anesthesiologistfee": "",
                            "operatingrmfee": "",
                            "housetourfee": "",
                            "hosmisousfee": "",
                            "noofdays": "",
                            "firstvisitdt": "",
                            "admissiondt": "",
                            // "location": "",
                            // "hospitalid": "",
                            // "otherhopsital": "",
                            "housingclass": "",
                            "diagnosis": "",
                            "medsigndt": "",
                            "Ansthsimthd": "",
                            "oprtn": "",
                            "attendDoctor": "",
                            "isBxNetwork": "",
                        })
                    });

                },
                modalConfirmPrimaryButtonText: i18n.t("translation:apply.step1_1.popup.btn1"),
                modalConfirmSecondaryButtonText: i18n.t("translation:apply.step1_1.popup.btn2"),
            });
        } else {
            setStep("1_2");

            step1_1_saveForm();

            checkIsStep2_1Valid(true);
            checkIsStep2_2Valid(true);
            checkIsStep2_3Valid(true);
            checkIsStep2_4Valid(true);
            checkIsStep2_5Valid(true);
        }

    },

    isError: false,
    setIsError: (value) => set({ isError: value }),

    initAPILoad: async (id) => {
        const { setIsError, resetApplyData, getHospitalList, setStep } = get();
        setIsError(false);

        try {
            if (id) {
                setStep("success");
            } else {
                resetApplyData();
                await getHospitalList();

                setStep("0");
            }
        } catch (error) {
            setIsError(true);
        }
    },

    // # Step 0
    // Data: 手術醫院 List
    hospitalList: [],
    getHospitalList: async () => {
        const response = await fetch(`${apiDomain}/${basename}/api/general/GetHospitalList`);
        const data = await response.json();
        set({ hospitalList: data.hospital })
    },

    // Value: 手術機構
    isQ1Option1Active: false,
    isQ1Option2Active: false,
    q1OptionError: false,
    setIsQ1Option1Active: (value) => set({ isQ1Option1Active: value }),
    setIsQ1Option2Active: (value) => set({ isQ1Option2Active: value }),
    setQ1OptionError: (value) => set({ q1OptionError: value }),

    // Value: 手術地點
    isQ2Option1Active: false,
    isQ2Option2Active: false,
    q2OptionError: false,
    setIsQ2Option1Active: (value) => set({ isQ2Option1Active: value }),
    setIsQ2Option2Active: (value) => set({ isQ2Option2Active: value }),
    setQ2OptionError: (value) => set({ q2OptionError: value }),

    // Value: 手術醫院 || 日間中心/門診名稱
    hospital: {},
    hospitalText: "",
    hospitalError1: false,
    hospitalError2: false,
    setHospital: (value) => set({ hospital: value }),
    setHospitalText: (value) => set({ hospitalText: value }),
    setHospitalError1: (value) => set({ hospitalError1: value }),
    setHospitalError2: (value) => set({ hospitalError2: value }),

    // Validation
    isStep0Valid: false,
    setIsStep0Valid: (value) => set({ isStep0Valid: value }),
    checkIsStep0Valid: (checkOnly) => {
        const { isQ1Option1Active, isQ1Option2Active, setQ1OptionError, isQ2Option1Active, isQ2Option2Active, setQ2OptionError, hospital, hospitalText, setHospitalError1, setHospitalError2 } = get();
        const { setIsStep0Valid } = get();
        setQ1OptionError(false);
        setQ2OptionError(false);
        setHospitalError1(false);
        setHospitalError2(false);

        // Q1 Validation
        if (!(isQ1Option1Active || isQ1Option2Active)) {
            if (!checkOnly) {
                setQ1OptionError(true);
            }

            setIsStep0Valid(false);
            return false;
        }

        // Q2 Validation
        if (!(isQ2Option1Active || isQ2Option2Active) && isQ1Option1Active) {
            if (!checkOnly) {
                setQ2OptionError(true);
            }

            setIsStep0Valid(false);
            return false;
        }

        // Hospital Validation
        // Q1 Option 1 + Q2 Option 1 + Hospital
        if (isQ1Option1Active && isQ2Option1Active && !hospital?.value) {
            if (!checkOnly) {
                setHospitalError1(true);
            }

            setIsStep0Valid(false);
            return false;
        }

        // Hospital Validation
        // Q1 Option 1 + Q2 Option 1 + Hospital Text
        // Q1 Option 1 + Q2 Option 2 + Hospital Text
        if (
            (isQ1Option1Active && isQ2Option1Active && hospital?.value === "00000000000000000000000000000000" && !hospitalText) ||
            (isQ1Option1Active && isQ2Option2Active && !hospitalText)
        ) {
            if (!checkOnly) {
                setHospitalError2(true);
            }

            setIsStep0Valid(false);
            return false;
        }

        // Q1 Option 2 + Q2 Option 1 + Hospital Text
        if (isQ1Option2Active && !hospitalText) {
            if (!checkOnly) {
                setHospitalError2(true);
            }

            setIsStep0Valid(false);
            return false;
        }

        setIsStep0Valid(true);
        return true;
    },

    // # Step 1_1
    // Value: 保單號碼
    step1_1_Q1: "",
    step1_1_Q1Error: false,
    setStep1_1_Q1: (value) => set({ step1_1_Q1: value }),
    setStep1_1_Q1Error: (value) => set({ step1_1_Q1Error: value }),

    // Value: 申請人
    step1_1_Q2_Type: "person",  // person, spouse_children
    setStep1_1_Q2_Type: (value) => set({ step1_1_Q2_Type: value }),

    // Value: 申請人受保人號碼
    step1_1_Q2: "",
    step1_1_Q2Error: false,
    setStep1_1_Q2: (value) => set({ step1_1_Q2: value }),
    setStep1_1_Q2Error: (value) => set({ step1_1_Q2Error: value }),

    // Value: 申請人受保人號碼
    step1_1_Q3: "",
    step1_1_Q3Error: false,
    setStep1_1_Q3: (value) => set({ step1_1_Q3: value }),
    setStep1_1_Q3Error: (value) => set({ step1_1_Q3Error: value }),

    // Value: 配偶或子女受保人號碼
    step1_1_Q4: "",
    step1_1_Q4Error: false,
    setStep1_1_Q4: (value) => set({ step1_1_Q4: value }),
    setStep1_1_Q4Error: (value) => set({ step1_1_Q4Error: value }),

    // Validation
    isStep1_1Valid: false,
    setIsStep1_1Valid: (value) => set({ isStep1_1Valid: value }),
    checkIsStep1_1Valid: (checkOnly) => {
        const { step1_1_Q1, step1_1_Q2, step1_1_Q2_Type, step1_1_Q3, step1_1_Q4, setStep1_1_Q1Error, setStep1_1_Q2Error, setStep1_1_Q3Error, setStep1_1_Q4Error, setIsStep1_1Valid } = get();
        let isValid = true;

        if (!checkOnly) {
            setStep1_1_Q1Error(false);
            setStep1_1_Q2Error(false);
            setStep1_1_Q3Error(false);
            setStep1_1_Q4Error(false);
        }

        if (!step1_1_Q1) {
            if (!checkOnly) {
                setStep1_1_Q1Error(true);
            }
            isValid = false;
        }

        if (step1_1_Q2_Type === "person") {
            if (!step1_1_Q2) {
                if (!checkOnly) {
                    setStep1_1_Q2Error(true);
                }
                isValid = false;
            }
        } else {
            if (!step1_1_Q3) {
                if (!checkOnly) {
                    setStep1_1_Q3Error(true);
                }
                isValid = false;
            }

            if (!step1_1_Q4) {
                if (!checkOnly) {
                    setStep1_1_Q4Error(true);
                }
                isValid = false;
            }
        }

        setIsStep1_1Valid(isValid);
        return isValid;
    },

    // Save Form
    step1_1_saveForm: async () => {
        const { isQ1Option1Active, isQ1Option2Active } = get();
        const { isQ2Option1Active, isQ2Option2Active } = get();
        const { hospital, hospitalText } = get();
        const { token, logout } = useAuthStore.getState();

        const operationloctype = isQ1Option1Active ? "HOSPITAL" : isQ1Option2Active ? "DAYCENTRE" : "";
        const location = isQ1Option2Active ? "HKMU" : isQ2Option1Active ? "HKMU" : "OTHER";

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/savePreAuthForm`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "operationloctype": operationloctype,
                "location": location,
                "hospitalid": isQ1Option1Active && isQ2Option1Active ? hospital?.value : "",
                "otherhopsital": (!(isQ1Option1Active && isQ2Option1Active) || hospital?.value === "00000000000000000000000000000000") ? hospitalText : "",
                "step": "2_2",
            })
        });

        if (response.status === 401) { // Unauthorized
            logout();
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            throw new Error('Network Error');
        }

        return await response.json();
    },

    // # Step 2_1
    isStep2_1Option1Active: false,
    setIsStep2_1Option1Active: (value) => set({ isStep2_1Option1Active: value }),
    isStep2_1Option2Active: false,
    setIsStep2_1Option2Active: (value) => set({ isStep2_1Option2Active: value }),
    isStep2_1Option3Active: false,
    setIsStep2_1Option3Active: (value) => set({ isStep2_1Option3Active: value }),
    step2_1Error: false,
    setStep2_1Error: (value) => set({ step2_1Error: value }),

    // Validation
    isStep2_1Valid: false,
    setIsStep2_1Valid: (value) => set({ isStep2_1Valid: value }),
    checkIsStep2_1Valid: (checkOnly) => {
        const { isStep2_1Option1Active, isStep2_1Option2Active, isStep2_1Option3Active, setStep2_1Error, setIsStep2_1Valid } = get();
        setStep2_1Error(false);

        if (!(isStep2_1Option1Active || isStep2_1Option2Active || isStep2_1Option3Active)) {
            if (!checkOnly) {
                setStep2_1Error(true);
            }

            setIsStep2_1Valid(false);
            return false;
        }

        setIsStep2_1Valid(true);
        return true;
    },

    // Save Form
    step2_1_saveForm: async () => {
        const { isStep2_1Option1Active, isStep2_1Option2Active, isStep2_1Option3Active, setStep } = get();
        const { token, logout } = useAuthStore.getState();
        let applicationtype = "";

        if (isStep2_1Option1Active) {
            applicationtype = "DOCFILL";
        } else if (isStep2_1Option2Active) {
            applicationtype = "BNET";
        } else if (isStep2_1Option3Active) {
            applicationtype = "NOTSURE";
        }

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/savePreAuthForm`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "step": "2_2",
                // "applicationtype": applicationtype,
                "applicationtype": "NOTSURE",
            })
        });

        if (response.status === 401) { // Unauthorized
            logout();
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            throw new Error('Network Error');
        }

        return await response.json();
    },

    // # Step 2_2
    // Value: Bnet號碼
    step2_2Bnet: "",
    step2_2BnetError: false,
    setStep2_2Bnet: (value) => set({ step2_2Bnet: value }),
    setStep2_2BnetError: (value) => set({ step2_2BnetError: value }),

    // Value: upload files
    step2_2MedicalDocuments: [],
    step2_2MedicalDocumentsError: false,
    step2_2Attachid: "",
    setStep2_2MedicalDocuments: (value) => set({ step2_2MedicalDocuments: value }),
    addStep2_2MedicalDocuments: newState =>
        set(prevState => {
            return {
                step2_2MedicalDocuments: [...prevState.step2_2MedicalDocuments, newState]
            }
        }),
    setStep2_2MedicalDocumentsError: (value) => set({ step2_2MedicalDocumentsError: value }),
    setStep2_2Attachid: (value) => set({ step2_2Attachid: value }),

    // Validation
    isStep2_2Valid: false,
    setIsStep2_2Valid: (value) => set({ isStep2_2Valid: value }),
    checkIsStep2_2Valid: (checkOnly) => {
        const { isStep2_1Option2Active } = get();
        const { step2_2Bnet, setStep2_2BnetError, step2_2MedicalDocuments, setStep2_2MedicalDocumentsError, setIsStep2_2Valid } = get();
        setStep2_2BnetError(false);
        setStep2_2MedicalDocumentsError(false);

        if (isStep2_1Option2Active) {
            if (!step2_2Bnet) {
                if (!checkOnly) {
                    setStep2_2BnetError(true);
                }

                setIsStep2_2Valid(false);
                return false;
            }

            const bnetRegex = /^B\d{2}(0[1-9]|1[0-2])\d{4}$/;
            if (!bnetRegex.test(step2_2Bnet)) {
                if (!checkOnly) {
                    setStep2_2BnetError(true);
                }
                setIsStep2_2Valid(false);
                return false;
            }

        } else {
            // if (!step2_2MedicalDocuments.length) {
            //     if (!checkOnly) {
            //         setStep2_2MedicalDocumentsError(true);
            //     }

            //     setIsStep2_2Valid(false);
            //     return false;
            // }
        }

        setIsStep2_2Valid(true);
        return true;
    },

    // Save Form
    step2_2_saveForm: async () => {
        const { isStep2_1Option2Active, step2_2Bnet, step2_2Attachid } = get();
        const { token, logout } = useAuthStore.getState();

        let response = null;
        if (isStep2_1Option2Active) {
            response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/savePreAuthForm`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    "step": "2_2",
                    "bnetno": step2_2Bnet,
                })
            });
        } else {
            response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/savePreAuthForm`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    "step": "2_2",
                    "appattachid": step2_2Attachid,
                })
            });
        }

        if (response.status === 401) { // Unauthorized
            logout();
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            throw new Error('Network Error');
        }

        return await response.json();
    },

    // # Step 2_3
    // Value: 外科醫生費用
    step2_3_Q1: "",
    setStep2_3_Q1: (value) => set({ step2_3_Q1: value }),
    step2_3_Q1Error: false,
    setStep2_3_Q1Error: (value) => set({ step2_3_Q1Error: value }),

    // Value: 麻醉科醫生費用
    step2_3_Q2: "",
    setStep2_3_Q2: (value) => set({ step2_3_Q2: value }),
    step2_3_Q2Error: false,
    setStep2_3_Q2Error: (value) => set({ step2_3_Q2Error: value }),

    // Value: 手術室費用
    step2_3_Q3: "",
    setStep2_3_Q3: (value) => set({ step2_3_Q3: value }),
    step2_3_Q3Error: false,
    setStep2_3_Q3Error: (value) => set({ step2_3_Q3Error: value }),

    // Value: 醫生巡房費用（日計）
    step2_3_Q4: "",
    setStep2_3_Q4: (value) => set({ step2_3_Q4: value }),
    step2_3_Q4Error: false,
    setStep2_3_Q4Error: (value) => set({ step2_3_Q4Error: value }),

    // Value: 醫院雜項費用
    step2_3_Q5: "",
    setStep2_3_Q5: (value) => set({ step2_3_Q5: value }),
    step2_3_Q5Error: false,
    setStep2_3_Q5Error: (value) => set({ step2_3_Q5Error: value }),

    // Value: 住院日數
    step2_3_Q6: "",
    setStep2_3_Q6: (value) => set({ step2_3_Q6: value }),
    step2_3_Q6Error: false,
    setStep2_3_Q6Error: (value) => set({ step2_3_Q6Error: value }),

    step2_3_Remark: "",
    setStep2_3_Remark: (value) => set({ step2_3_Remark: value }),

    // Validation
    step2_3_ScrollTo: "",
    setStep2_3_ScrollTo: (value) => set({ step2_3_ScrollTo: value }),
    isStep2_3Valid: false,
    setIsStep2_3Valid: (value) => set({ isStep2_3Valid: value }),
    checkIsStep2_3Valid: (checkOnly) => {
        const { setStep2_3_ScrollTo, setIsStep2_3Valid } = get();
        const { step2_3_Q1, setStep2_3_Q1Error } = get();
        const { step2_3_Q2, setStep2_3_Q2Error } = get();
        const { step2_3_Q3, setStep2_3_Q3Error } = get();
        const { step2_3_Q4, setStep2_3_Q4Error } = get();
        const { step2_3_Q5, setStep2_3_Q5Error } = get();
        const { step2_3_Q6, setStep2_3_Q6Error } = get();
        setStep2_3_Q1Error(false);
        setStep2_3_Q2Error(false);
        setStep2_3_Q3Error(false);
        setStep2_3_Q4Error(false);
        setStep2_3_Q5Error(false);
        setStep2_3_Q6Error(false);
        let isValid = true;

        if (!step2_3_Q6) {
            if (!checkOnly) {
                setStep2_3_Q6Error(true);
                setStep2_3_ScrollTo("dayNumber");
            }
            isValid = false;
        }

        if (!step2_3_Q4) {
            if (!checkOnly) {
                setStep2_3_Q4Error(true);
                setStep2_3_ScrollTo("doctorFee");
            }
            isValid = false;
        }

        if (!step2_3_Q5) {
            if (!checkOnly) {
                setStep2_3_Q5Error(true);
                setStep2_3_ScrollTo("miscellaneous");
            }
            isValid = false;
        }

        if (!step2_3_Q3) {
            if (!checkOnly) {
                setStep2_3_Q3Error(true);
                setStep2_3_ScrollTo("operatingRoomFee");
            }
            isValid = false;
        }

        if (!step2_3_Q2) {
            if (!checkOnly) {
                setStep2_3_Q2Error(true);
                setStep2_3_ScrollTo("anesthesiologistFee");
            }
            isValid = false;
        }

        if (!step2_3_Q1) {
            if (!checkOnly) {
                setStep2_3_Q1Error(true);
                setStep2_3_ScrollTo("surgeonFee");
            }
            isValid = false;
        }

        setIsStep2_3Valid(isValid);
        return isValid;
    },

    // Save Form
    step2_3_saveForm: async () => {
        const { step2_3_Q1, step2_3_Q2, step2_3_Q3, step2_3_Q4, step2_3_Q5, step2_3_Q6, step2_3_Remark } = get();
        const { token, logout } = useAuthStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/savePreAuthForm`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "step": "2_3",
                "surgeonfee": step2_3_Q1,
                "anesthesiologistfee": step2_3_Q2,
                "operatingrmfee": step2_3_Q3,
                "housetourfee": step2_3_Q4,
                "hosmisousfee": step2_3_Q5,
                "noofdays": step2_3_Q6,
                "surgeryBudgetRemark": step2_3_Remark,
            })
        });

        if (response.status === 401) { // Unauthorized
            logout();
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            throw new Error('Network Error');
        }

        return await response.json();
    },

    // # Step 2_4
    // Value: 首次到診日期
    step2_4FirstVisit: "",
    setStep2_4FirstVisit: (value) => set({ step2_4FirstVisit: value }),
    step2_4FirstVisitError: false,
    setStep2_4FirstVisitError: (value) => set({ step2_4FirstVisitError: value }),

    // Value: 入院/治療日期
    step2_4AdmissionDate: "",
    setStep2_4AdmissionDate: (value) => set({ step2_4AdmissionDate: value }),
    step2_4AdmissionDateError: false,
    setStep2_4AdmissionDateError: (value) => set({ step2_4AdmissionDateError: value }),

    // Value: 住房級別
    step2_4HousingClassOption1: false,
    step2_4HousingClassOption2: false,
    step2_4HousingClassOption3: false,
    step2_4HousingClassOption4: false,
    step2_4HousingClassOption5: false,
    step2_4HousingClassError: false,
    setStep2_4HousingClassOption1: (value) => set({ step2_4HousingClassOption1: value }),
    setStep2_4HousingClassOption2: (value) => set({ step2_4HousingClassOption2: value }),
    setStep2_4HousingClassOption3: (value) => set({ step2_4HousingClassOption3: value }),
    setStep2_4HousingClassOption4: (value) => set({ step2_4HousingClassOption4: value }),
    setStep2_4HousingClassOption5: (value) => set({ step2_4HousingClassOption5: value }),
    setStep2_4HousingClassError: (value) => set({ step2_4HousingClassError: value }),

    // Value: 診斷及相關病徵
    step2_4Diagnosis: "",
    step2_4DiagnosisError: false,
    setStep2_4Diagnosis: (value) => set({ step2_4Diagnosis: value }),
    setStep2_4DiagnosisError: (value) => set({ step2_4DiagnosisError: value }),

    // Value: 相似病徵已存在多久 ( i.e.首次病發日期 )
    step2_4SymptomDuration: "",
    step2_4SymptomDurationError: false,
    setStep2_4SymptomDuration: (value) => set({ step2_4SymptomDuration: value }),
    setStep2_4SymptomDurationError: (value) => set({ step2_4SymptomDurationError: value }),

    // Value: 麻醉方式
    anesthesiaMethodOption1: false,
    anesthesiaMethodOption2: false,
    anesthesiaMethodOption3: false,
    anesthesiaMethodOption4: false,
    anesthesiaMethodOption5: false,
    anesthesiaMethodError: false,
    setAnesthesiaMethodOption1: (value) => set({ anesthesiaMethodOption1: value }),
    setAnesthesiaMethodOption2: (value) => set({ anesthesiaMethodOption2: value }),
    setAnesthesiaMethodOption3: (value) => set({ anesthesiaMethodOption3: value }),
    setAnesthesiaMethodOption4: (value) => set({ anesthesiaMethodOption4: value }),
    setAnesthesiaMethodOption5: (value) => set({ anesthesiaMethodOption5: value }),
    setAnesthesiaMethodError: (value) => set({ anesthesiaMethodError: value }),

    // Value: 相似病徵已存在多久 ( i.e.首次病發日期 )
    surgeryToBePerformed: "",
    surgeryToBePerformedError: false,
    setSurgeryToBePerformed: (value) => set({ surgeryToBePerformed: value }),
    setSurgeryToBePerformedError: (value) => set({ surgeryToBePerformedError: value }),

    // Validation
    step2_4_ScrollTo: "",
    setStep2_4_ScrollTo: (value) => set({ step2_4_ScrollTo: value }),
    isStep2_4Valid: false,
    setIsStep2_4Valid: (value) => set({ isStep2_4Valid: value }),
    checkIsStep2_4Valid: (checkOnly) => {
        const { isQ1Option1Active, isQ1Option2Active, setQ1OptionError, isQ2Option1Active, isQ2Option2Active, setQ2OptionError, hospital, hospitalText, setHospitalError1, setHospitalError2 } = get();
        const { step2_4FirstVisit, step2_4AdmissionDate, setStep2_4FirstVisitError, setStep2_4AdmissionDateError } = get();
        const { step2_4HousingClassOption1, step2_4HousingClassOption2, step2_4HousingClassOption3, step2_4HousingClassOption4, step2_4HousingClassOption5, setStep2_4HousingClassError } = get();
        const { step2_4Diagnosis, setStep2_4DiagnosisError } = get();
        const { step2_4SymptomDuration, setStep2_4SymptomDurationError } = get();
        const { anesthesiaMethodOption1, anesthesiaMethodOption2, anesthesiaMethodOption3, anesthesiaMethodOption4, anesthesiaMethodOption5, setAnesthesiaMethodError } = get();
        const { surgeryToBePerformed, setSurgeryToBePerformedError } = get();
        const { step2_4_ScrollTo, setStep2_4_ScrollTo, setIsStep2_4Valid } = get();
        let isValid = true;
        let scrollToEnabled = false;
        setQ1OptionError(false);
        setQ2OptionError(false);
        setHospitalError1(false);
        setHospitalError2(false);
        setStep2_4FirstVisitError(false);
        setStep2_4AdmissionDateError(false);

        // hospital Q1 Validation
        if (!(isQ1Option1Active || isQ1Option2Active)) {
            if (!checkOnly) {
                setQ1OptionError(true);

                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("hospital");
                    scrollToEnabled = true;
                }
            }

            isValid = false;
        }

        // hospital Q2 Validation
        if (!(isQ2Option1Active || isQ2Option2Active) && isQ1Option1Active) {
            if (!checkOnly) {
                setQ2OptionError(true);

                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("hospital");
                    scrollToEnabled = true;
                }
            }

            isValid = false;
        }

        // Hospital Validation
        // Q1 Option 1 + Q2 Option 1 + Hospital
        if (isQ1Option1Active && isQ2Option1Active && !hospital?.value) {
            if (!checkOnly) {
                setHospitalError1(true);

                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("hospital");
                    scrollToEnabled = true;
                }
            }

            isValid = false;
        }

        // Hospital Validation
        // Q1 Option 1 + Q2 Option 1 + Hospital Text
        // Q1 Option 1 + Q2 Option 2 + Hospital Text
        if (
            (isQ1Option1Active && isQ2Option1Active && hospital?.value === "00000000000000000000000000000000" && !hospitalText) ||
            (isQ1Option1Active && isQ2Option2Active && !hospitalText)
        ) {
            if (!checkOnly) {
                setHospitalError2(true);

                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("hospital");
                    scrollToEnabled = true;
                }
            }

            isValid = false;
        }

        // Q1 Option 2 + Q2 Option 1 + Hospital Text
        if (isQ1Option2Active && !hospitalText) {
            if (!checkOnly) {
                setHospitalError2(true);

                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("hospital");
                    scrollToEnabled = true;
                }
            }

            isValid = false;
        }

        if (!(step2_4HousingClassOption1 || step2_4HousingClassOption2 || step2_4HousingClassOption3 || step2_4HousingClassOption4 || step2_4HousingClassOption5)) {
            if (!checkOnly) {
                setStep2_4HousingClassError(true);
                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("housingClass");
                    scrollToEnabled = true;
                }
            }
            isValid = false;
        }

        if (!step2_4FirstVisit) {
            if (!checkOnly) {
                setStep2_4FirstVisitError(true);
                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("firstVisit");
                    scrollToEnabled = true;
                }
            }
            isValid = false;
        }

        if (!step2_4AdmissionDate) {
            if (!checkOnly) {
                setStep2_4AdmissionDateError(true);
                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("admissionDate");
                    scrollToEnabled = true;
                }
            }
            isValid = false;
        }

        if (!step2_4Diagnosis) {
            if (!checkOnly) {
                setStep2_4DiagnosisError(true);
                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("diagnosis");
                    scrollToEnabled = true;
                }
            }
            isValid = false;
        }

        if (!step2_4SymptomDuration) {
            if (!checkOnly) {
                setStep2_4SymptomDurationError(true);
                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("symptomDuration");
                    scrollToEnabled = true;
                }
            }
            isValid = false;
        }

        if (!(anesthesiaMethodOption1 || anesthesiaMethodOption2 || anesthesiaMethodOption3 || anesthesiaMethodOption4 || anesthesiaMethodOption5)) {
            if (!checkOnly) {
                setAnesthesiaMethodError(true);
                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("anesthesiaMethod");
                    scrollToEnabled = true;
                }
            }
            isValid = false;
        }

        if (!surgeryToBePerformed) {
            if (!checkOnly) {
                setSurgeryToBePerformedError(true);
                if (!scrollToEnabled) {
                    setStep2_4_ScrollTo("surgeryToBePerformed");
                    scrollToEnabled = true;
                }
            }
            isValid = false;
        }

        setIsStep2_4Valid(isValid);
        return isValid;
    },

    // Save Form
    step2_4_saveForm: async () => {
        const { step2_4FirstVisit, step2_4AdmissionDate } = get();
        const { isQ1Option1Active, isQ1Option2Active } = get();
        const { isQ2Option1Active, isQ2Option2Active } = get();
        const { hospital, hospitalText } = get();
        const { step2_4HousingClassOption1, step2_4HousingClassOption2, step2_4HousingClassOption3, step2_4HousingClassOption4, step2_4HousingClassOption5 } = get();
        const { step2_4Diagnosis } = get();
        const { step2_4SymptomDuration } = get();
        const { anesthesiaMethodOption1, anesthesiaMethodOption2, anesthesiaMethodOption3, anesthesiaMethodOption4, anesthesiaMethodOption5 } = get();
        const { surgeryToBePerformed } = get();
        const { token, logout } = useAuthStore.getState();

        const housingclass = step2_4HousingClassOption1 ? "REGULAR" : step2_4HousingClassOption2 ? "SEMI" : step2_4HousingClassOption3 ? "PRIVATE" : step2_4HousingClassOption4 ? "DAYCASE" : "OUTPATIENT_CLINICAL";
        const ansthsimthd = anesthesiaMethodOption1 ? "NONEED" : anesthesiaMethodOption2 ? "PARTIAL" : anesthesiaMethodOption3 ? "FULL" : anesthesiaMethodOption4 ? "MONITOR" : "UNKNOWN";
        const location = isQ1Option2Active ? "HKMU" : isQ2Option1Active ? "HKMU" : "OTHER";

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/savePreAuthForm`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "step": "2_4",
                "firstvisitdt": step2_4FirstVisit,
                "admissiondt": step2_4AdmissionDate,
                "operationloctype": isQ1Option1Active ? "HOSPITAL" : "DAYCENTRE",
                "location": location,
                "hospitalid": isQ1Option1Active && isQ2Option1Active ? hospital?.value : "",
                "otherhopsital": (!(isQ1Option1Active && isQ2Option1Active) || hospital?.value === "00000000000000000000000000000000") ? hospitalText : "",
                "housingclass": housingclass,
                "diagnosis": step2_4Diagnosis,
                "medsigndt": step2_4SymptomDuration.substr(0, 10),
                "Ansthsimthd": ansthsimthd,
                "oprtn": surgeryToBePerformed,
            })
        });

        if (response.status === 401) { // Unauthorized
            logout();
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            throw new Error('Network Error');
        }

        return await response.json();
    },

    // # Step 2_5
    // Value: 請問您的主診／外科醫生屬於：
    step2_5Option1Active: false,
    setStep2_5Option1Active: (value) => set({ step2_5Option1Active: value }),
    step2_5Option2Active: false,
    setStep2_5Option2Active: (value) => set({ step2_5Option2Active: value }),
    step2_5OptionError: false,
    setStep2_5OptionError: (value) => set({ step2_5OptionError: value }),

    // Value: 主診醫生／外科醫生名字
    step2_5DocterName: "",
    setStep2_5DocterName: (value) => set({ step2_5DocterName: value }),

    // Validation
    isStep2_5Valid: false,
    setIsStep2_5Valid: (value) => set({ isStep2_5Valid: value }),
    checkIsStep2_5Valid: (checkOnly) => {
        const { step2_5Option1Active, step2_5Option2Active, setStep2_5OptionError, setIsStep2_5Valid } = get();
        setStep2_5OptionError(false);

        if (!(step2_5Option1Active || step2_5Option2Active)) {
            if (!checkOnly) {
                setStep2_5OptionError(true);
            }

            setIsStep2_5Valid(false);
            return false;
        }

        setIsStep2_5Valid(true);
        return true;
    },

    // Save Form
    step2_5_saveForm: async () => {
        const { step2_5Option1Active, step2_5Option2Active, step2_5DocterName } = get();
        const { token, logout } = useAuthStore.getState();

        const isBxNetwork = !(step2_5Option1Active || step2_5Option2Active) ? "" : step2_5Option1Active ? "Y" : "N";

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/savePreAuthForm`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "step": "2_5",
                "isBxNetWork": isBxNetwork,
                "attendDoctor": step2_5DocterName,
            })
        });

        if (response.status === 401) { // Unauthorized
            logout();
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            throw new Error('Network Error');
        }

        return await response.json();
    },


    // # Step 3_1
    isStep3_1Accepted: false,
    isStep3_1AcceptedError: false,
    setIsStep3_1Accepted: (value) => set({ isStep3_1Accepted: value }),
    setIsStep3_1AcceptedError: (value) => set({ isStep3_1AcceptedError: value }),

    // Validation
    isStep3_1Valid: false,
    setStep3_1Valid: (value) => set({ isStep3_1Valid: value }),
    checkIsStep3_1Valid: (checkOnly) => {
        const { isStep3_1Accepted, setIsStep3_1AcceptedError, setStep3_1Valid } = get();
        setIsStep3_1AcceptedError(false);

        if (!isStep3_1Accepted) {
            if (!checkOnly) {
                setIsStep3_1AcceptedError(true);
            }

            setStep3_1Valid(false);
            return false;
        }

        setStep3_1Valid(true);
        return true;
    },

    // # Step 4_1
    step4_option1: true,
    setStep4_option1: (value) => set({ step4_option1: value }),
    step4_option2: false,
    setStep4_option2: (value) => set({ step4_option2: value }),

    isStep4_1Accepted: false,
    setIsStep4_1Accepted: (value) => set({ isStep4_1Accepted: value }),
    step4_1Error: false,
    setStep4_1Error: (value) => set({ step4_1Error: value }),

    // Payment
    getMpgsSession: async (lang_payment) => {
        // lang_payment: en_us, zh_hk

        const { token, logout } = useAuthStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthCore/getMpgsSession?lang=${lang_payment}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });

        if (response.status === 401) { // Unauthorized
            logout();
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            throw new Error('Network Error');
        }

        return await response.json();
    },
}))

export default useApplyStore;